import { useEffect, useState } from 'react'
import RoutesMap from 'route_control/RouteControl'
import SidebarMenu from 'route_control/SidebarMenu'
import * as Icons from 'lucide-react'
import Menu from 'antd/es/menu'
import ConfigProvider from 'antd/es/config-provider'
import { Drawer } from 'antd'
import PerfectScrollbar from 'react-perfect-scrollbar'
import 'react-perfect-scrollbar/dist/css/styles.css'
import logo from '../../assets/images/express-logo.png'
import logoFull from 'assets/images/express-logo-full.png'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { signOut } from '../../redux/slices/assets'

export default function Sidebar(props) {

    const { collapsed, setCollapsed, drawerOpen, setDrawerOpen, isMobileOrTablet } = props

    const [stateOpenKeys, setStateOpenKeys] = useState([])
    const [selectedKeys, setSelectedKeys] = useState([])
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const DynamicIcon = ({ iconName, ...props }) => {
        const IconComponent = Icons[iconName];

        if (!IconComponent) {
            // Handle cases where the icon name doesn't exist
            return <span>Icon not found</span>;
        }

        return <IconComponent {...props} />;
    }

    // Static customer-portal menu (route_control/SidebarMenu.js)
    const item = SidebarMenu.map((parent, p_index) => (
        parent.children ?
            {
                key: p_index.toString(),
                icon: <DynamicIcon iconName={parent.icon} size={16} />,
                label: parent.label,
                children: parent.children.map((child, c_index) => ({
                    key: p_index.toString() + '-' + c_index.toString(),
                    icon: <DynamicIcon iconName={'CircleDot'} size={16} />,
                    label: <Link to={child.path} onClick={() => setDrawerOpen(false)}>{child.name}</Link>,
                }))
            }
            :
            {
                key: p_index.toString(),
                icon: <DynamicIcon iconName={parent.icon} size={16} />,
                label: <Link to={parent.route} onClick={() => setDrawerOpen(false)}>{parent.label}</Link>,
            }
    ))

    const getLevelKeys = (items1) => {
        const key = {};
        const func = (items2, level = 1) => {
            items2?.forEach((item) => {
                if (item?.key) {
                    key[item.key] = level;
                }
                if (item?.children) {
                    func(item.children, level + 1);
                }
            });
        };
        func(items1);
        return key;
    };

    const levelKeys = getLevelKeys(item);

    const onOpenChange = (openKeys) => {
        const currentOpenKey = openKeys.find((key) => stateOpenKeys.indexOf(key) === -1);
        // open
        if (currentOpenKey !== undefined) {
            const repeatIndex = openKeys
                .filter((key) => key !== currentOpenKey)
                .findIndex((key) => levelKeys[key] === levelKeys[currentOpenKey]);
            setStateOpenKeys(
                openKeys
                    // remove repeat key
                    .filter((_, index) => index !== repeatIndex)
                    // remove current level all child
                    .filter((key) => levelKeys[key] <= levelKeys[currentOpenKey]),
            );
        } else {
            // close
            setStateOpenKeys(openKeys);
        }
    };

    // Portal has no logout API — clear session locally and go to login
    const logout = () => {
        dispatch(signOut()).then(() => navigate(RoutesMap.LOGIN.path))
    }

    useEffect(() => {
        // Derive selected and open keys from current URL
        const pathName = location?.pathname || ''
        let computedSelectedKey = null
        let computedOpenKey = null

        item?.forEach((parent) => {
            const parentRoute = parent?.label?.props?.to
            if (parentRoute && (pathName === parentRoute || pathName.startsWith(parentRoute + '/'))) {
                computedSelectedKey = parent?.key
            }
            parent?.children?.forEach((child) => {
                const route = child?.label?.props?.to
                if (!route) return
                if (pathName === route || pathName.startsWith(route + '/')) {
                    computedSelectedKey = child?.key
                    computedOpenKey = parent?.key
                }
            })
        })

        if (computedSelectedKey) {
            setSelectedKeys([computedSelectedKey])
        }
        if (computedOpenKey) {
            setStateOpenKeys([computedOpenKey])
        }
    }, [location?.pathname])

    return (
        <>
            {!isMobileOrTablet && (
                <div className={collapsed ? 'sidebar_collapsed' : 'sidebar_notCollapsed'}>

                    {collapsed ?
                        <div className='sidebar_header d-flex gap-2 align-items-center justify-content-center'>
                            <img src={logo} className='img-fluid' alt="logo" />
                        </div>
                        :
                        <div className='sidebar_header d-flex gap-2 align-items-center justify-content-center'>
                            <img src={logoFull} className='sidebar_logo_full' alt="Ethernet Xpress" />
                        </div>
                    }

                    <ConfigProvider
                        theme={{
                            components: {
                                Menu: {
                                    itemSelectedColor: '#0891B2',
                                    subMenuItemSelectedColor: '#0891B2',
                                    itemBg: '#F1F5F9',
                                    itemHoverBg: '#E2E8F0',
                                    itemHoverColor: '#0F172A',
                                    itemSelectedBg: '#E2E8F0',
                                    itemColor: '#000000',
                                    popupBg: '#F1F5F9'
                                },
                            },
                            token: {
                                fontFamily: 'Inter',
                                fontSize: 13,
                            },
                        }}
                    >
                        <PerfectScrollbar style={{
                            background: '#F1F5F9',
                            overflow: 'auto',
                            height: '100vh',
                            position: 'relative',
                            insetInlineStart: 0,
                        }}>
                            <Menu
                                mode="inline"
                                inlineCollapsed={collapsed}
                                selectedKeys={selectedKeys}
                                openKeys={stateOpenKeys}
                                onOpenChange={onOpenChange}
                                items={item}
                                style={{ backgroundColor: '#F1F5F9', marginBottom: '130px' }}
                            />
                            <div className={`px-4 py-3 sidebar_logout ${collapsed ? 'sidebar_logout_collapsed' : 'sidebar_logout_expanded'}`}>
                                {collapsed ?
                                    <Icons.Power size={18} onClick={() => logout()} />
                                    :
                                    <div className='d-flex gap-2 align-items-center' onClick={() => logout()}>
                                        <Icons.Power size={18} />
                                        <span className='fw-semibold text-secondary' style={{ fontSize: '12px' }}>Log Out</span>
                                    </div>
                                }
                            </div>
                        </PerfectScrollbar>
                    </ConfigProvider>
                </div>
            )}

            {isMobileOrTablet && (
                <Drawer
                    title={
                        <div className='sidebar_header d-flex gap-2 align-items-center justify-content-center'>
                            <img src={logoFull} className='sidebar_logo_full' alt="Ethernet Xpress" />
                        </div>
                    }
                    placement="left"
                    rootClassName="sidebar_drawer"
                    closable
                    onClose={() => setDrawerOpen(false)}
                    open={drawerOpen}
                    zIndex={2000}
                    styles={{
                        body: { padding: 0, background: "#F1F5F9" },
                        header: { background: "#F1F5F9" },  // optional
                        footer: { background: "#F1F5F9" }   // optional
                    }}
                >
                    <ConfigProvider
                        theme={{
                            components: {
                                Menu: {
                                    itemSelectedColor: '#0891B2',
                                    subMenuItemSelectedColor: '#0891B2',
                                    itemBg: '#F1F5F9',
                                    itemHoverBg: '#E2E8F0',
                                    itemHoverColor: '#0F172A',
                                    itemSelectedBg: '#E2E8F0',
                                    itemColor: '#64748B',
                                    popupBg: '#F1F5F9'
                                },
                            },
                            token: {
                                fontFamily: 'Inter',
                                fontSize: 13,
                            },
                        }}
                    >
                        <PerfectScrollbar style={{
                            background: '#F1F5F9',
                            overflow: 'auto',
                            height: '100vh',
                            position: 'relative',
                            insetInlineStart: 0,
                        }}>
                            <Menu
                                mode="inline"
                                inlineCollapsed={collapsed}
                                selectedKeys={selectedKeys}
                                openKeys={stateOpenKeys}
                                onOpenChange={onOpenChange}
                                items={item}
                                style={{ backgroundColor: '#F1F5F9', marginBottom: '130px' }}
                            />
                            <div className={`d-flex gap-2 align-items-center px-4 py-3 sidebar_logout ${collapsed ? 'sidebar_logout_collapsed' : 'sidebar_logout_expanded'}`}>
                                {collapsed ?
                                    <Icons.Power size={18} onClick={() => logout()} />
                                    :
                                    <div onClick={() => logout()}>
                                        <Icons.Power size={18} />
                                        <span className='fw-semibold text-secondary' style={{ fontSize: '12px' }}>Log Out</span>
                                    </div>
                                }
                            </div>
                        </PerfectScrollbar>
                    </ConfigProvider>
                </Drawer>
            )}
        </>
    )
}